package app.nestbridge.feature.invitations.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Email
import androidx.compose.material.icons.outlined.Phone
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.components.CoreBackButton
import com.karawek.core.ui.components.CoreChip
import com.karawek.core.ui.components.CorePhoneTextField
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.components.CoreTextField
import com.karawek.core.ui.theme.CoreShapes
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.feature.invitations.model.InvitationDeliveryMethod
import app.nestbridge.feature.invitations.screen.InvitationDetailEvent
import app.nestbridge.feature.invitations.screen.InvitationDetailState
import app.nestbridge.feature.invitations.screen.SendInvitationEvent
import app.nestbridge.feature.invitations.screen.SendInvitationScreen
import app.nestbridge.feature.invitations.screen.SendInvitationState
import app.nestbridge.feature.invitations.screen.canSend
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import nestbridge.composeapp.generated.resources.Res
import nestbridge.composeapp.generated.resources.ic_nestbridge_logo
import org.jetbrains.compose.resources.painterResource
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = SendInvitationScreen::class,
    scope = AppScope::class,
)
@Composable
fun SendInvitationUi(
    state: SendInvitationState,
    modifier: Modifier = Modifier,
) {
    CoreScaffold(
        title = "Invite a professional",
        subtitle = "Send an invitation to a therapist or provider to create a care team for ${state.childDisplayName ?: "Child"}.",
        footer = { Footer(state) },
        onBack = { state.eventSink(SendInvitationEvent.Back) },
        onAppear = { state.eventSink(SendInvitationEvent.OnAppear) },
        onAppearKey = state.childDisplayName ?: "",
        modifier = modifier,
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(CoreSpacing.xs),
                horizontalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
            ) {
                    CoreChip(
                        selected = state.selectedMethod == InvitationDeliveryMethod.PHONE,
                        label = "Phone",
                        leadingIcon = Icons.Outlined.Phone,
                        modifier = Modifier.weight(1f),
                        onClick = {
                            state.eventSink(SendInvitationEvent.SelectMethod(InvitationDeliveryMethod.PHONE))
                        },
                    )

                CoreChip(
                    selected = state.selectedMethod == InvitationDeliveryMethod.EMAIL,
                    label = "Email",
                    leadingIcon = Icons.Outlined.Email,
                    modifier = Modifier.weight(1f),
                    onClick = {
                        state.eventSink(SendInvitationEvent.SelectMethod(InvitationDeliveryMethod.EMAIL))
                    },
                )
            }

            Column(
                verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
            ) {
                if (state.selectedMethod == InvitationDeliveryMethod.PHONE) {
                    CorePhoneTextField(
                        value = state.emailOrPhone,
                        onValueChange = { state.eventSink(SendInvitationEvent.UpdateEmailOrPhone(it)) },
                        label = "Professional's phone number",
                        placeholder = "(555) 123-4567",
                        modifier = Modifier.fillMaxWidth(),
                        error = state.errorMessage,
                    )
                } else {
                    CoreTextField(
                        value = state.emailOrPhone,
                        onValueChange = { state.eventSink(SendInvitationEvent.UpdateEmailOrPhone(it)) },
                        label = "Professional's email address",
                        placeholder = "therapist@example.com",
                        modifier = Modifier.fillMaxWidth(),
                        error = state.errorMessage,
                    )
                }

                Text(
                    text = "They will receive an invitation to create a care team for ${state.childDisplayName ?: "Child"}.",
                    style = CoreTypography.bodySecondary,
                    color = CoreTheme.colors.textSecondary,
                )
            }
        }
    }
}

@Composable
private fun Footer(state: SendInvitationState) =
    Column(
        verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
    ) {
        CorePrimaryButton(
            text = if (state.isSending) "Sending..." else "Send invitation",
            enabled = state.canSend(),
            onClick = { state.eventSink(SendInvitationEvent.SendInvitation) },
            modifier = Modifier
                .fillMaxWidth(),
        )

        Text(
            text = "The professional will need to verify their identity before accepting.",
            style = CoreTypography.footnote,
            color = CoreTheme.colors.textSecondary,
        )
    }

@Preview
@Composable
private fun SendInvitationUiPhonePreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        SendInvitationUi(
            state = SendInvitationState(
                childDisplayName = "Sofia",
                selectedMethod = InvitationDeliveryMethod.PHONE,
                emailOrPhone = "",
                isSending = false,
                errorMessage = null,
                eventSink = {},
            ),
        )
    }
}

@Preview
@Composable
private fun SendInvitationUiPhoneFilledPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        SendInvitationUi(
            state = SendInvitationState(
                childDisplayName = "Sofia",
                selectedMethod = InvitationDeliveryMethod.PHONE,
                emailOrPhone = "555-555-0101",
                isSending = false,
                errorMessage = null,
                eventSink = {},
            ),
        )
    }
}

@Preview
@Composable
private fun SendInvitationUiEmailPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        SendInvitationUi(
            state = SendInvitationState(
                childDisplayName = "Sofia",
                selectedMethod = InvitationDeliveryMethod.EMAIL,
                emailOrPhone = "",
                isSending = false,
                errorMessage = null,
                eventSink = {},
            ),
        )
    }
}

@Preview
@Composable
private fun SendInvitationUiEmailFilledPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        SendInvitationUi(
            state = SendInvitationState(
                childDisplayName = "Sofia",
                selectedMethod = InvitationDeliveryMethod.EMAIL,
                emailOrPhone = "user@karawek.com",
                isSending = false,
                errorMessage = null,
                eventSink = {},
            ),
        )
    }
}
